import React, { useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HomeOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import { useClientTenantStore } from '../../store/clientTenantStore';
import { useTelegramViewport } from '../../hooks/useTelegramViewport';
import { useRefreshOnVisibility } from '../../hooks/useRefreshOnVisibility';
import HomeTab from './HomeTab';
import BookingsTab from './BookingsTab';
import ProfileTab from './ProfileTab';
import BookingDetailsPage from './BookingDetailsPage';
import CancellationPage from './CancellationPage';
import BookingFlowPage from './BookingFlowPage';

// Bottom navni yashirish kerak bo'lgan path pattern'lar
const HIDE_NAV_PATTERNS = [/^\/booking\/new/, /^\/bookings\/.+/];

function shouldHideNav(pathname: string): boolean {
  return HIDE_NAV_PATTERNS.some((pattern) => pattern.test(pathname));
}

// Bottom Nav komponent
interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/', labelKey: 'nav.home', icon: <HomeOutlined style={{ fontSize: 24 }} /> },
  { path: '/bookings', labelKey: 'nav.bookings', icon: <CalendarOutlined style={{ fontSize: 24 }} /> },
  { path: '/profile', labelKey: 'nav.profile', icon: <UserOutlined style={{ fontSize: 24 }} /> },
];

function BottomNav() {
  const { t } = useTranslation('customer');
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = NAV_ITEMS.find((item) =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path),
  );

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        borderTop: '1px solid var(--bl-divider, #ebebeb)',
        backgroundColor: 'var(--bl-surface, #ffffff)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        zIndex: 100,
      }}
    >
      {NAV_ITEMS.map((item) => {
        const isActive = activeTab?.path === item.path;
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 0',
              border: 'none',
              borderTop: isActive
                ? '2px solid var(--bl-primary, #2481cc)'
                : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              color: isActive
                ? 'var(--bl-primary, #2481cc)'
                : 'var(--bl-text-secondary, #999)',
              fontSize: 11,
              fontWeight: 500,
              gap: 2,
            }}
          >
            {item.icon}
            <span>{t(item.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function ClientShell() {
  const location = useLocation();
  const { business, fetchBootstrap } = useClientTenantStore();
  const hideNav = shouldHideNav(location.pathname);
  const viewportHeight = useTelegramViewport();

  useEffect(() => {
    if (!business) {
      void fetchBootstrap();
    }
  }, []); // intentional: run once on mount

  // Til o'zgargandagi refetch clientTenantStore modul-darajasi listenerida —
  // shu fayldagi useEffect Profile tabida til o'zgarganda ishlamasligi
  // mumkin, chunki React mounting bir vaqtda boshqa joyga ko'chmaydi.

  // When the WebApp is re-activated (e.g. user returned after pressing
  // Android hardware back), re-expand so Telegram gives us full viewport
  // again. Without this, the stable viewport can stay at 0 and the UI
  // collapses to a blank screen.
  const handleReopen = useCallback(() => {
    WebApp.expand();
  }, []);
  useRefreshOnVisibility(handleReopen);

  return (
    <div style={{ height: viewportHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <Routes>
          <Route path="/" element={<HomeTab />} />
          <Route path="/bookings" element={<BookingsTab />} />
          <Route path="/profile" element={<ProfileTab />} />
          <Route path="/booking/new/:step" element={
            <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
              <BookingFlowPage />
            </div>
          } />
          <Route path="/bookings/:id" element={
            <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
              <BookingDetailsPage />
            </div>
          } />
          <Route path="/bookings/:id/cancel" element={
            <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
              <CancellationPage />
            </div>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {!hideNav && <BottomNav />}
    </div>
  );
}
